'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Compass, Coffee, Sunrise, ArrowRight, ChevronDown, ChevronUp, CheckCircle2, Loader2, Shield, X, Dumbbell, Wrench, Calendar, RefreshCw } from 'lucide-react';
import type { ReentryState } from '@/lib/reentryState';
import { buildReturnPlan, type ReturnPlan } from '@/lib/returnToTrainingPlan';
import { useAuth } from '@/lib/AuthContext';
import {
  acceptReentryPlan,
  loadAcceptedReentryPlan,
  clearAcceptedReentryPlan,
  intensityFactorFor,
  rampWeekOf,
  isReentryFriendlyProgram,
  pickRampProgram,
  SESSIONS_PER_RAMP_WEEK,
  type AcceptedReentryPlan,
  type ReentryMode,
} from '@/lib/acceptedReentryPlan';

interface Props {
  state: ReentryState | null;
  /** Days remaining in the user's current program (for tailored copy). Optional. */
  programWeek?: number | null;
}

/**
 * Welcome-back / drift / fresh-start banner with actionable reentry plan.
 *
 *   - returning (14+ days off, no plan accepted): show the plan + "Accept" CTA
 *   - returning (plan accepted): show ramp-week progress + cap factor
 *   - drifting (7-13 days off): light nudge to log something
 *   - fresh:     onboarding nudge
 *
 * Returns null when status is 'on-track' AND no accepted plan is active.
 */
export default function ReentryBanner({ state }: Props) {
  const { user } = useAuth();
  const router = useRouter();
  const [expanded, setExpanded] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [acceptedPlan, setAcceptedPlan] = useState<AcceptedReentryPlan | null>(null);
  const [planLoaded, setPlanLoaded] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [hasProgram, setHasProgram] = useState(false);
  const [currentProgramId, setCurrentProgramId] = useState<string | null>(null);
  const [experienceLevel, setExperienceLevel] = useState<'beginner' | 'intermediate' | 'advanced' | null>(null);

  useEffect(() => {
    if (!user?.uid) {
      setPlanLoaded(true);
      return;
    }
    loadAcceptedReentryPlan(user.uid)
      .then(setAcceptedPlan)
      .catch(() => {})
      .finally(() => setPlanLoaded(true));

    // Detect whether user has a program AND capture its ID so the picker
    // can recommend a dedicated ramp-up program when the user's current
    // program isn't reentry-friendly.
    import('@/lib/programManagement')
      .then(({ getUserProgramSelection }) => getUserProgramSelection(user.uid))
      .then((sel) => {
        setHasProgram(!!sel);
        setCurrentProgramId(sel?.programId ?? null);
      })
      .catch(() => {});

    // Experience level drives the right ramp recommendation. Pulled from
    // users/{uid}.experienceLevel (set during onboarding).
    (async () => {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db: firestoreDb } = await import('@/lib/firebase');
        const snap = await getDoc(doc(firestoreDb, 'users', user.uid));
        if (snap.exists()) {
          const lvl = (snap.data() as { experienceLevel?: string }).experienceLevel;
          if (lvl === 'beginner' || lvl === 'intermediate' || lvl === 'advanced') {
            setExperienceLevel(lvl);
          }
        }
      } catch { /* best-effort */ }
    })();
  }, [user?.uid]);

  // Auto-accept reentry plan in 'program' mode when the user is on a
  // reentry-friendly program. The program IS the ramp — there's nothing
  // to choose. Skip the picker entirely. Fires once after both the
  // reentry state and the current program are loaded.
  //
  // IMPORTANT: this useEffect MUST be declared before any conditional early
  // returns below — Rules of Hooks. Moving it after the
  // `if (acceptedPlan && planLoaded) return ...` block caused React #310
  // ('Rendered fewer hooks than expected') when acceptedPlan flipped from
  // null → set inside this same effect.
  useEffect(() => {
    if (!user?.uid) return;
    if (!state || state.status !== 'returning') return;
    if (acceptedPlan) return;                     // already accepted
    if (!planLoaded) return;                       // need definitive load
    if (!isReentryFriendlyProgram(currentProgramId)) return;
    if (accepting) return;

    const plan = buildReturnPlan(state);
    if (!plan) return;

    setAccepting(true);
    acceptReentryPlan(user.uid, plan, state.daysSinceLastSession, { mode: 'program' })
      .then(setAcceptedPlan)
      .catch(err => console.error('Auto-accept (reentry-friendly program) failed:', err))
      .finally(() => setAccepting(false));
  }, [user?.uid, state, planLoaded, acceptedPlan, currentProgramId, accepting]);

  // Active accepted plan — render the "plan in progress" UI regardless of
  // current reentry classification (since the user IS technically training
  // again, classification will say 'on-track').
  //
  // Exception: brand-new users with zero session history (status === 'fresh',
  // daysSinceLastSession === Infinity) should NEVER see a "Welcome back" /
  // return plan, even if a stale acceptedReentryPlan doc exists from prior
  // testing on the same uid. That would make the app look broken to a
  // first-time user.
  const isTrulyFresh = !state || state.status === 'fresh' || !isFinite(state.daysSinceLastSession);
  if (acceptedPlan && planLoaded && !isTrulyFresh) {
    const week = rampWeekOf(acceptedPlan);
    if (week === 0) {
      // Ramp complete locally — the workout generator clears it from Firestore
      // next time it runs; nothing to render here.
      return null;
    }
    const factor = intensityFactorFor(acceptedPlan) ?? 1;
    return (
      <ReentryPlanActivePanel
        plan={acceptedPlan}
        week={week}
        factor={factor}
        onChangeMode={async () => {
          if (!user?.uid) return;
          if (!confirm('Change ramp mode? Your current ramp progress will be discarded so you can pick a new mode.')) return;
          try {
            await clearAcceptedReentryPlan(user.uid);
            setAcceptedPlan(null);
            // Banner will re-render in 'returning' branch and the user can
            // open the picker again.
          } catch (err) {
            console.error('Failed to clear reentry plan for mode change:', err);
          }
        }}
        onEndEarly={async () => {
          if (!user?.uid) return;
          if (!confirm('End the reentry ramp early? Future sessions will return to full intensity.')) return;
          try {
            await clearAcceptedReentryPlan(user.uid);
            setAcceptedPlan(null);
          } catch (err) {
            console.error('Failed to clear reentry plan:', err);
          }
        }}
      />
    );
  }

  if (!state || !planLoaded) return null;
  if (state.status === 'on-track') return null;

  if (state.status === 'returning') {
    const plan = buildReturnPlan(state);
    if (!plan) return null;

    // While the auto-accept effect is firing for a reentry-friendly
    // program, suppress the picker so the user doesn't see it flash.
    if (isReentryFriendlyProgram(currentProgramId) && accepting) return null;

    const handleAccept = async (mode: ReentryMode) => {
      if (!user?.uid) return;
      setAccepting(true);
      try {
        const accepted = await acceptReentryPlan(user.uid, plan, state.daysSinceLastSession, { mode });
        setAcceptedPlan(accepted);
        setExpanded(false);
        setShowPicker(false);
        // Route the user where they want to ramp.
        if (mode === 'reconditioning') {
          router.push('/session/reconditioning');
        } else if (mode === 'freestyle') {
          router.push('/session/freestyle');
        }
        // Program mode stays on dashboard — the rewind already landed them
        // at the right week/day, and they'll hit the program's session next.
      } catch (err) {
        console.error('Failed to accept reentry plan:', err);
        alert('Could not accept the plan. Check your connection and try again.');
      } finally {
        setAccepting(false);
      }
    };

    // Atomic switch-to-ramp-program + accept-plan in one click. Used by
    // the "Switch to recommended" button so the user doesn't have to
    // bounce through the change-programs page and then re-accept.
    const handleSwitchToRamp = async (programId: string) => {
      if (!user?.uid) return;
      setAccepting(true);
      try {
        // 1. Switch the user's selected program to the ramp.
        const { saveUserProgramSelection } = await import('@/lib/programManagement');
        await saveUserProgramSelection(user.uid, {
          programId,
          programLevel: 'operator',
          startDate: new Date(),
          currentWeek: 1,
          currentDay: 1,
          completedDays: [],
          skippedDays: [],
        });
        // 2. Accept the reentry plan in program mode. Since the program
        // is reentry-friendly, smartWorkoutGenerator will bypass the cap;
        // the plan exists so the active ramp panel renders and the
        // session counter ticks correctly.
        const accepted = await acceptReentryPlan(user.uid, plan, state.daysSinceLastSession, { mode: 'program' });
        setAcceptedPlan(accepted);
        setCurrentProgramId(programId);
        setShowPicker(false);
        // Force a refresh so other surfaces (dashboard, schedule)
        // re-fetch the new program data.
        router.refresh();
      } catch (err) {
        console.error('Failed to switch to ramp program:', err);
        alert('Could not switch programs. Check your connection and try again.');
      } finally {
        setAccepting(false);
      }
    };

    return (
      <ReentryReturningPanel
        state={state}
        plan={plan}
        expanded={expanded}
        accepting={accepting}
        showPicker={showPicker}
        hasProgram={hasProgram}
        currentProgramIsReentryFriendly={isReentryFriendlyProgram(currentProgramId)}
        experienceLevel={experienceLevel}
        onToggle={() => setExpanded((v) => !v)}
        onOpenPicker={() => setShowPicker(true)}
        onClosePicker={() => setShowPicker(false)}
        onPickMode={handleAccept}
        onSwitchToRamp={handleSwitchToRamp}
      />
    );
  }

  if (state.status === 'drifting') {
    return (
      <div className="rounded-xl border border-amber-500/30 bg-amber-950/30 px-4 py-3 flex items-start gap-3">
        <Coffee className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" aria-hidden />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-100">
            {state.headline}
          </p>
          <p className="text-[13px] text-amber-200/70 mt-0.5 leading-relaxed">
            One short session this week keeps the streak alive. No need to swing for the fences.
          </p>
        </div>
      </div>
    );
  }

  // fresh
  return (
    <div className="rounded-xl border border-blue-500/30 bg-blue-950/30 px-4 py-3 flex items-start gap-3">
      <Compass className="w-5 h-5 text-blue-300 mt-0.5 flex-shrink-0" aria-hidden />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-blue-100">
          {state.headline}
        </p>
        <p className="text-[13px] text-blue-200/70 mt-0.5 leading-relaxed">
          Log your first session — even a short one — so the system can start tailoring your training.
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Returning user — plan not yet accepted
// ============================================================================
function ReentryReturningPanel({
  state, plan, expanded, accepting, showPicker, hasProgram, currentProgramIsReentryFriendly, experienceLevel, onToggle, onOpenPicker, onClosePicker, onPickMode, onSwitchToRamp,
}: {
  state: ReentryState;
  plan: ReturnPlan;
  expanded: boolean;
  accepting: boolean;
  showPicker: boolean;
  hasProgram: boolean;
  currentProgramIsReentryFriendly: boolean;
  experienceLevel: 'beginner' | 'intermediate' | 'advanced' | null;
  onToggle: () => void;
  onOpenPicker: () => void;
  onClosePicker: () => void;
  onPickMode: (mode: ReentryMode) => void;
  onSwitchToRamp: (programId: string) => void;
}) {
  return (
    <div className="relative rounded-xl overflow-hidden border-2 border-emerald-500/40 bg-gradient-to-r from-emerald-950/80 via-operator-slate to-operator-void">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)' }} />
      <div className="relative px-4 py-3.5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
            <Sunrise className="w-5 h-5 text-emerald-400" aria-hidden />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-400 mb-0.5">
              Operator returning
            </p>
            <h3 className="text-base font-bold text-white leading-tight">
              {state.headline}
            </h3>
            <p className="text-[13px] text-emerald-100/80 mt-1 leading-relaxed">
              {plan.summary}
            </p>

            <div className="flex flex-wrap items-center gap-2 mt-3">
              <button
                onClick={onOpenPicker}
                disabled={accepting || showPicker}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-500 text-emerald-950 text-xs font-bold uppercase tracking-wider hover:bg-emerald-400 transition disabled:opacity-50"
              >
                {accepting ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
                    Accepting…
                  </>
                ) : (
                  <>
                    <Shield className="w-3.5 h-3.5" aria-hidden />
                    Accept Plan
                  </>
                )}
              </button>
              <button
                onClick={onToggle}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-emerald-500/40 text-emerald-300 text-xs font-bold uppercase tracking-wider hover:bg-emerald-500/10 transition"
              >
                {expanded ? 'Hide details' : 'See details'}
                {expanded ? <ChevronUp className="w-3 h-3" aria-hidden /> : <ChevronDown className="w-3 h-3" aria-hidden />}
              </button>
              {state.missedNutrition && (
                <span className="text-[12px] text-emerald-200/70">
                  Nutrition log paused too — <Link href="/fuel" className="underline hover:text-white">restart at /fuel</Link>
                </span>
              )}
            </div>

            {/* Mode picker — appears in place when user taps Accept Plan */}
            {showPicker && (
              <div className="mt-4 border-t border-emerald-500/20 pt-3 space-y-2 animate-fadeIn">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-bold">
                    How do you want to ramp back?
                  </p>
                  <button
                    onClick={onClosePicker}
                    aria-label="Cancel"
                    className="text-emerald-300/60 hover:text-emerald-300 p-1 -m-1"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>

                {hasProgram && (
                  <button
                    onClick={() => onPickMode('program')}
                    disabled={accepting}
                    className="w-full text-left grid grid-cols-[28px_1fr_16px] items-center gap-3 px-3 py-3 rounded-md border border-amber-500/40 bg-amber-500/[0.04] hover:bg-amber-500/[0.1] transition disabled:opacity-50"
                  >
                    <Calendar className="w-5 h-5 text-amber-400" aria-hidden />
                    <div>
                      <div className="font-bold text-white text-[13px]">Stick with my program</div>
                      <div className="text-[11px] text-amber-200/70 mt-0.5">
                        {currentProgramIsReentryFriendly
                          ? 'Your program is already a ramp — no extra cap will be stacked on top.'
                          : `Rewind to W2 D1, ramp caps applied to every program session.`}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-amber-400" aria-hidden />
                  </button>
                )}

                {/* Recommend a dedicated ramp-up program when the user's program
                    is NOT reentry-friendly. Clicking is ATOMIC — switches the
                    program AND accepts the reentry plan in one step. The user
                    expects "switch to this ramp" to actually do the switch,
                    not redirect them to the change-programs page. */}
                {hasProgram && !currentProgramIsReentryFriendly && (() => {
                  const rec = pickRampProgram(state.daysSinceLastSession, experienceLevel);
                  if (!rec) return null;
                  return (
                    <button
                      onClick={() => onSwitchToRamp(rec.programId)}
                      disabled={accepting}
                      className="w-full text-left grid grid-cols-[28px_1fr_16px] items-center gap-3 px-3 py-3 rounded-md border border-purple-500/40 bg-purple-500/[0.04] hover:bg-purple-500/[0.1] transition disabled:opacity-50"
                    >
                      <RefreshCw className={`w-5 h-5 text-purple-300 ${accepting ? 'animate-spin' : ''}`} aria-hidden />
                      <div>
                        <div className="font-bold text-white text-[13px]">Switch to {rec.name} (recommended)</div>
                        <div className="text-[11px] text-purple-200/70 mt-0.5">
                          {rec.rationale}
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-purple-300" aria-hidden />
                    </button>
                  );
                })()}

                <button
                  onClick={() => onPickMode('freestyle')}
                  disabled={accepting}
                  className="w-full text-left grid grid-cols-[28px_1fr_16px] items-center gap-3 px-3 py-3 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] hover:bg-emerald-500/[0.1] transition disabled:opacity-50"
                >
                  <Dumbbell className="w-5 h-5 text-emerald-400" aria-hidden />
                  <div>
                    <div className="font-bold text-white text-[13px]">Ease back via freestyle</div>
                    <div className="text-[11px] text-emerald-200/70 mt-0.5">
                      You pick the lifts. Every weight prefill auto-scaled to your ramp cap.
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-emerald-400" aria-hidden />
                </button>

                <button
                  onClick={() => onPickMode('reconditioning')}
                  disabled={accepting}
                  className="w-full text-left grid grid-cols-[28px_1fr_16px] items-center gap-3 px-3 py-3 rounded-md border border-hud-cyan/40 bg-hud-cyan/[0.04] hover:bg-hud-cyan/[0.1] transition disabled:opacity-50"
                >
                  <Wrench className="w-5 h-5 text-hud-cyan" aria-hidden />
                  <div>
                    <div className="font-bold text-white text-[13px]">Today: reconditioning session</div>
                    <div className="text-[11px] text-hud-cyan/80 mt-0.5">
                      No decisions. Full-body session built from your 1RMs × ramp factor.
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-hud-cyan" aria-hidden />
                </button>
              </div>
            )}

            {/* Expanded plan details */}
            {expanded && (
              <div className="mt-4 space-y-3 border-t border-emerald-500/20 pt-3">
                {/* Quick stats */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-emerald-950/40 border border-emerald-500/20 rounded-md py-2">
                    <div className="font-mono text-[9px] uppercase tracking-wider text-emerald-300/70">Fitness retained</div>
                    <div className="font-mono text-lg font-bold text-emerald-300 tabular-nums">{plan.fitnessRetained}%</div>
                  </div>
                  <div className="bg-emerald-950/40 border border-emerald-500/20 rounded-md py-2">
                    <div className="font-mono text-[9px] uppercase tracking-wider text-emerald-300/70">Week 1 load</div>
                    <div className="font-mono text-lg font-bold text-emerald-300 tabular-nums">{plan.week1IntensityPct}%</div>
                  </div>
                  <div className="bg-emerald-950/40 border border-emerald-500/20 rounded-md py-2">
                    <div className="font-mono text-[9px] uppercase tracking-wider text-emerald-300/70">Ramp weeks</div>
                    <div className="font-mono text-lg font-bold text-emerald-300 tabular-nums">{plan.rampWeeks}</div>
                  </div>
                </div>

                {/* Actions */}
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-emerald-300/70 mb-1.5">What we'll do for you</p>
                  <ul className="space-y-1.5">
                    {plan.actions.map((action, i) => (
                      <li key={i} className="flex items-start gap-2 text-[13px] text-emerald-50/90 leading-relaxed">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" aria-hidden />
                        <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Expectations */}
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-amber-300/70 mb-1.5">What to expect</p>
                  <ul className="space-y-1 text-[12px] text-emerald-50/70 leading-relaxed">
                    {plan.expectations.map((exp, i) => (
                      <li key={i} className="pl-5 relative before:content-['•'] before:absolute before:left-1.5 before:text-amber-300/70">
                        {exp}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="text-right">
                  <span className="font-mono text-[10px] text-emerald-300/50">
                    Plan confidence: {Math.round(plan.confidence * 100)}%
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Accepted plan — show ramp progress
// ============================================================================
function ReentryPlanActivePanel({
  plan, week, factor, onEndEarly, onChangeMode,
}: {
  plan: AcceptedReentryPlan;
  week: number;
  factor: number;
  onEndEarly: () => void;
  onChangeMode: () => void;
}) {
  const sessionsDone = plan.rampSessionsCompleted ?? 0;
  // Progress is the share of completed sessions across the full ramp. Caps
  // at 100% when sessionsDone >= rampWeeks * SESSIONS_PER_RAMP_WEEK.
  const totalRampSessions = plan.rampWeeks * SESSIONS_PER_RAMP_WEEK;
  const pct = Math.min(100, Math.round((sessionsDone / totalRampSessions) * 100));
  const sessionsThisWeek = sessionsDone - (week - 1) * SESSIONS_PER_RAMP_WEEK;
  return (
    <div className="relative rounded-xl overflow-hidden border-2 border-emerald-500/40 bg-gradient-to-r from-emerald-950/80 via-operator-slate to-operator-void">
      <div className="relative px-4 py-3.5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-emerald-400" aria-hidden />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-400">
                Reentry Ramp · Active · {plan.mode ? plan.mode.toUpperCase() : 'PROGRAM'}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={onChangeMode}
                  aria-label="Change ramp mode"
                  title="Change ramp mode"
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-emerald-500/40 text-emerald-300/80 hover:text-emerald-300 hover:bg-emerald-500/10 transition font-mono text-[9px] uppercase tracking-[0.18em]"
                >
                  <RefreshCw className="w-3 h-3" aria-hidden />
                  Change
                </button>
                <button
                  onClick={onEndEarly}
                  aria-label="End reentry ramp early"
                  className="text-emerald-300/60 hover:text-red-400 transition p-1 -m-1"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <h3 className="text-base font-bold text-white leading-tight">
              Week {week} of {plan.rampWeeks} — capping at <span className="text-emerald-300 tabular-nums">{Math.round(factor * 100)}%</span> of pre-break load
            </h3>
            <p className="text-[13px] text-emerald-100/80 mt-1 leading-relaxed">
              <span className="text-emerald-300 tabular-nums">{Math.max(0, sessionsThisWeek)}/{SESSIONS_PER_RAMP_WEEK}</span> sessions this ramp week. Cap relaxes after {SESSIONS_PER_RAMP_WEEK} sessions OR 1 calendar week — whichever is slower.
              {plan.programRewindDays > 0 && ` Program rewound to W2 D1.`}
            </p>

            {/* Ramp progress bar */}
            <div className="mt-3">
              <div className="h-2 bg-emerald-950 border border-emerald-500/20 rounded overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-500 to-emerald-300 transition-all shadow-[0_0_6px_rgba(74,222,128,0.5)]"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="flex justify-between mt-1 font-mono text-[9px] uppercase tracking-wider text-emerald-300/60">
                <span>Start</span>
                <span>Ramp complete</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-3 font-mono text-[10px] uppercase tracking-wider text-emerald-300/70">
              <span>
                Was off: <span className="text-white tabular-nums">{plan.daysOffAtAccept}d</span>
              </span>
              <span>·</span>
              <span>
                Fitness retained: <span className="text-white tabular-nums">{plan.fitnessRetained}%</span>
              </span>
            </div>

            <Link
              href="/session"
              className="mt-3 inline-flex items-center gap-1 text-[13px] font-bold text-emerald-300 hover:text-white transition"
            >
              Start today's session
              <ArrowRight className="w-3.5 h-3.5" aria-hidden />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
